/**
 * ServiceHealthDashboard - Панель мониторинга здоровья сервисов
 * 
 * Отображает статус всех внешних сервисов:
 * - Stock провайдеры (Unsplash, Pexels, Pixabay)
 * - Human fallback сервисы (XSGames, Pravatar, RandomUser)
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../utils/api';

interface RateLimitInfo {
  limit: number | null;
  remaining: number | null;
  used: number | null;
  usagePercent: number | null;
  resetAt: string | null;
  resetIn: string | null;
}

interface ServiceStatus {
  id: string;
  name: string;
  type: string;
  available: boolean | null;
  rateLimited?: boolean;
  configured: boolean;
  lastCheck: string | null;
  lastError: string | null;
  errorCount: number;
  successCount: number;
  responseTime: number | null;
  quality: string | number;
  supportsGender?: boolean;
  priority?: number;
  description: string;
  rateLimit?: RateLimitInfo | null;
}

interface HealthSummary {
  totalServices: number;
  availableStock: number;
  availableFallback: number;
  unchecked: number;
  lastFullCheck: string | null;
}

interface HealthData {
  stockProviders: ServiceStatus[];
  humanFallbacks: ServiceStatus[];
  summary: HealthSummary;
}

const ServiceHealthDashboard: React.FC = () => {
  const [healthData, setHealthData] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Загрузка статуса (с автоматической проверкой лимитов)
  const fetchStatus = useCallback(async (withCheck = false) => {
    try {
      setLoading(true);
      
      // Если нужна проверка - сначала делаем её
      if (withCheck) {
        await axios.post('/api/admin/services/check');
      }
      
      const response = await axios.get('/api/admin/services/status');
      const data = response.data;
      
      if (data.success) {
        setHealthData(data.data);
        setError(null);
      } else {
        setError(data.error || 'Ошибка загрузки статуса');
      }
    } catch (err) {
      setError('Не удалось загрузить статус сервисов');
    } finally {
      setLoading(false);
    }
  }, []);

  // Проверка всех сервисов
  const checkAllServices = async () => {
    try {
      setChecking(true);
      const response = await axios.post('/api/admin/services/check');
      const data = response.data;
      
      if (data.success) {
        // Перезагружаем статус после проверки
        await fetchStatus();
      } else {
        setError(data.error || 'Ошибка проверки сервисов');
      }
    } catch (err) {
      setError('Не удалось проверить сервисы');
    } finally {
      setChecking(false);
    }
  };

  // Проверка одного сервиса
  const checkService = async (serviceId: string) => {
    try {
      const response = await axios.post(`/api/admin/services/check/${serviceId}`);
      const data = response.data;
      
      if (data.success) {
        await fetchStatus();
      }
    } catch (err) {
      console.error('Ошибка проверки сервиса:', err);
    }
  };

  useEffect(() => {
    // При первой загрузке делаем проверку чтобы получить актуальные лимиты
    fetchStatus(true);
  }, [fetchStatus]);

  const getStatusPill = (service: ServiceStatus) => {
    if (!service.configured) return <span className="u2-pill is-warn">Не настроен</span>;
    if (service.available === null) return <span className="u2-pill is-warn">Не проверен</span>;
    if (service.rateLimited) return <span className="u2-pill is-warn">Rate limit</span>;
    if (service.available) return <span className="u2-pill is-ok">Работает</span>;
    return <span className="u2-pill is-bad">Недоступен</span>;
  };

  if (loading && !healthData) {
    return (
      <section className="u2-card is-gradient">
        <div className="u2-cardInner">
          <div className="u2-strong">Загрузка…</div>
          <div className="u2-muted" style={{ fontSize: 12, marginTop: 6 }}>
            Получаем статус сервисов.
          </div>
        </div>
      </section>
    );
  }

  return (
    <div className="u2-grid" style={{ gap: 18 }}>
      <section className="u2-card is-gradient">
        <div className="u2-cardInner">
          <div className="u2-cardHeader">
            <div>
              <h1 className="u2-title" style={{ fontSize: 22 }}>Мониторинг сервисов</h1>
              <p className="u2-subtitle">Стоковые провайдеры и fallback‑сервисы для human‑изображений.</p>
            </div>
            <button className="u2-btn is-primary" type="button" onClick={checkAllServices} disabled={checking}>
              {checking ? 'Проверка…' : 'Проверить все'}
            </button>
          </div>

          {error ? (
            <div className="u2-card is-soft" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
              <div className="u2-cardInner">
                <span className="u2-pill is-bad">ошибка</span>
                <div className="u2-muted" style={{ fontSize: 12, marginTop: 6 }}>{error}</div>
              </div>
            </div>
          ) : null}

          {healthData ? (
            <>
              <div className="u2-grid cols-4">
                <div className="u2-card" style={{ boxShadow: 'none', background: 'var(--u2-surface-3)' }}>
                  <div className="u2-cardInner">
                    <div className="u2-kpiLabel">Всего сервисов</div>
                    <div className="u2-kpiValue">{healthData.summary.totalServices}</div>
                  </div>
                </div>
                <div className="u2-card" style={{ boxShadow: 'none', background: 'var(--u2-surface-3)' }}>
                  <div className="u2-cardInner">
                    <div className="u2-kpiLabel">Доступно</div>
                    <div className="u2-kpiValue">{healthData.summary.availableStock + healthData.summary.availableFallback}</div>
                  </div>
                </div>
                <div className="u2-card" style={{ boxShadow: 'none', background: 'var(--u2-surface-3)' }}>
                  <div className="u2-cardInner">
                    <div className="u2-kpiLabel">Недоступно</div>
                    <div className="u2-kpiValue">
                      {healthData.summary.totalServices -
                        healthData.summary.availableStock -
                        healthData.summary.availableFallback -
                        healthData.summary.unchecked}
                    </div>
                  </div>
                </div>
                <div className="u2-card" style={{ boxShadow: 'none', background: 'var(--u2-surface-3)' }}>
                  <div className="u2-cardInner">
                    <div className="u2-kpiLabel">Не проверено</div>
                    <div className="u2-kpiValue">{healthData.summary.unchecked}</div>
                  </div>
                </div>
              </div>

              <div className="u2-divider" />

              <div className="u2-cardHeader">
                <div>
                  <h2 className="u2-title">Стоковые провайдеры изображений</h2>
                  <p className="u2-subtitle">Используются для генерации фоновых/контентных изображений.</p>
                </div>
              </div>

              <div className="u2-tableWrap" aria-label="Стоковые провайдеры">
                <table className="u2-table">
                  <thead>
                    <tr>
                      <th>Сервис</th>
                      <th>Статус</th>
                      <th>Отклик</th>
                      <th>Rate limit</th>
                      <th>Ошибка</th>
                      <th style={{ textAlign: 'right' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {healthData.stockProviders.map((service) => (
                      <tr key={service.id}>
                        <td>
                          <div className="u2-strong">{service.name}</div>
                          <div className="u2-muted" style={{ fontSize: 12 }}>{service.description}</div>
                        </td>
                        <td>{getStatusPill(service)}</td>
                        <td className="u2-muted" style={{ fontSize: 12 }}>
                          {service.responseTime ? `${service.responseTime}ms` : '—'}
                        </td>
                        <td className="u2-muted" style={{ fontSize: 12 }}>
                          {service.rateLimit?.limit
                            ? `${service.rateLimit.used ?? 0}/${service.rateLimit.limit} (осталось: ${service.rateLimit.remaining ?? 0})`
                            : '—'}
                        </td>
                        <td className="u2-muted" style={{ fontSize: 12 }}>
                          {service.lastError || '—'}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button className="u2-btn is-secondary" type="button" onClick={() => checkService(service.id)}>
                            Проверить
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="u2-divider" />

              <div className="u2-cardHeader">
                <div>
                  <h2 className="u2-title">Fallback‑сервисы для аватаров</h2>
                  <p className="u2-subtitle">Используются, если стоки не дают результата для human‑изображений.</p>
                </div>
              </div>

              <div className="u2-tableWrap" aria-label="Fallback сервисы для аватаров">
                <table className="u2-table">
                  <thead>
                    <tr>
                      <th>Сервис</th>
                      <th>Приоритет</th>
                      <th>Статус</th>
                      <th>Отклик</th>
                      <th>Фильтр пола</th>
                      <th>Ошибка</th>
                      <th style={{ textAlign: 'right' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {healthData.humanFallbacks
                      .slice()
                      .sort((a, b) => (a.priority || 99) - (b.priority || 99))
                      .map((service) => (
                        <tr key={service.id}>
                          <td>
                            <div className="u2-strong">{service.name}</div>
                            <div className="u2-muted" style={{ fontSize: 12 }}>{service.description}</div>
                          </td>
                          <td className="u2-muted" style={{ fontSize: 12 }}>#{service.priority ?? '—'}</td>
                          <td>{getStatusPill(service)}</td>
                          <td className="u2-muted" style={{ fontSize: 12 }}>
                            {service.responseTime ? `${service.responseTime}ms` : '—'}
                          </td>
                          <td className="u2-muted" style={{ fontSize: 12 }}>
                            {service.supportsGender ? 'Да' : 'Нет'}
                          </td>
                          <td className="u2-muted" style={{ fontSize: 12 }}>{service.lastError || '—'}</td>
                          <td style={{ textAlign: 'right' }}>
                            <button className="u2-btn is-secondary" type="button" onClick={() => checkService(service.id)}>
                              Проверить
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {healthData.summary.lastFullCheck ? (
                <div className="u2-muted" style={{ fontSize: 12, marginTop: 12 }}>
                  Последняя полная проверка: {new Date(healthData.summary.lastFullCheck).toLocaleString('ru-RU')}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </section>
    </div>
  );
};

export default ServiceHealthDashboard;

